import React from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import Recaptcha from '@prisjakt/react-google-invisible-recaptcha';
import PropTypes from 'prop-types';
import Breadcrumb from '@prisjakt/ui-breadcrumb';
import { AppContext } from '@prisjakt/ui-app-context';

import PageContent from '../../components/page-content';
import PageHeader from '../../components/page-header';

import './sagas';

import {
  createUserRequest,
  validateInput,
  loginWithCredentials,
  fetchMe,
  passwordRecoveryRequest,
  setNewPasswordRequest,
} from './requests';

import { isValidEmail, isCapsLockOn, getCurrentRef } from './helpers';

import accountActions from './actions';
import { promptToast } from '../../components/toast-container/actions';

import Signup from './signup';
import Login from './login';
import RecoverEmail from './forgot-password/recover-email';
import RecoverSetNewPassword from './forgot-password/recover-set-new-password';

const getRecaptchaKey = () => (IS_BROWSER ? window.RECAPTCHA : '');

const getErrors = (error, errorOrigin = 'form') => {
  const errors = {
    form: [],
    email: [],
    username: [],
    password: [],
    userAgreement: [],
    socialNetworks: [],
  };
  // Validation errors
  if (error && Array.isArray(error)) {
    error.reduce((allErrors, err) => {
      if (allErrors[err.type]) {
        allErrors[err.type].push(err);
      } else {
        // eslint-disable-next-line no-param-reassign
        allErrors[err.type] = [err];
      }

      return allErrors;
    }, errors);
  }
  // Other errors
  if (error && !Array.isArray(error)) {
    const emailErrors = [
      'registration.email-address-banned',
      'registration.email-in-use',
      'registration.invalid-email',
      'auth.invalid-parameter',
    ];

    const passwordErrors = ['auth.invalid-parameter'];

    const formErrors = [
      'registration.username-banned',
      'registration.username-taken',
      'registration.ip-address-banned',
      'registration.external-credentials-invalid',
      'registration.external-credentials-in-use',
      'registration.general-error',
      'auth.invalid-user-credentials',
      'auth.user-not-verified',
      'forgot-password.email.general-error',
      'forgot-password.email.not-registered',
      'new-password.link-expired-or-wrong',
    ];

    const socialNetworksErrors = [
      ...emailErrors,
      ...formErrors,
      'auth.wrong-market',
    ];

    const socialNetworksIgnoreErrors = ['auth.popup-closed'];

    if (formErrors.includes(error.message) && errorOrigin === 'form') {
      errors.form = [{ code: error.message }];
    }

    if (emailErrors.includes(error.message) && errorOrigin === 'form') {
      errors.email = [{ code: error.message }];
    }

    if (passwordErrors.includes(error.message) && errorOrigin === 'form') {
      errors.password = [{ code: error.message }];
    }

    if (
      Object.keys(errors).reduce(
        (count, key) => errors[key].length + count,
        0
      ) === 0 &&
      errorOrigin === 'form'
    ) {
      errors.form = [{ type: 'form', code: 'error.unknown' }];
    }

    if (errorOrigin === 'socialNetworks') {
      if (socialNetworksErrors.includes(error.code)) {
        errors.socialNetworks = [error];
      } else if (socialNetworksIgnoreErrors.includes(error.code)) {
        errors.socialNetworks = [];
      } else {
        errors.socialNetworks = [{ code: error.code || 'external.unknown' }];
      }
    }
  }

  return Object.keys(errors).reduce((errs, key) => {
    if (errors[key].length === 0) {
      return errs;
    }
    return Object.assign(errs, { [key]: errors[key] });
  }, {});
};

class AccountManager extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      errors: {},
      pending: false,
      socialPending: false,
    };

    this.recaptcha = {
      getResponse: () => '',
      reset: () => {},
      execute: () => {},
    };
  }

  renderView(props) {
    switch (this.props.routeName) {
      case 'signup':
        return <Signup {...props} />;
      case 'login':
        return <Login {...props} />;
      case 'forgotPassword':
        return <RecoverEmail {...props} />;
      case 'forgotPasswordChange':
        return <RecoverSetNewPassword {...props} />;
      default:
        return null;
    }
  }

  socialResponse(error, result) {
    this.setState({
      socialPending: false,
    });

    if (error) {
      this.setState({
        errors: getErrors(error, 'socialNetworks'),
      });
    } else if (result.success) {
      const { t } = this.context;

      fetchMe()
        .then(({ me }) => {
          this.props.userReceive(me);

          this.props.promptToast('success', t('account', 'login.successful'));

          this.props.history.push(getCurrentRef().path);
        })
        .catch(err => {
          throw err;
        });
    }
  }

  socialInit() {
    this.setState({
      socialPending: true,
    });
  }

  createUser(inputs) {
    const { location } = this.props;
    const recaptcha = this.recaptcha.getResponse();

    this.setState({
      pending: true,
      errors: {},
    });

    createUserRequest(inputs, recaptcha)
      .then(({ success, error }) => {
        if (success) {
          // @TODO: Save email in session for display on confirm page
          this.props.history.push(`/signup/confirm${location.search}`);
        } else {
          this.setState({
            pending: false,
            errors: getErrors(error, 'form'),
          });
        }
      })
      .catch(err => {
        throw err;
      });
  }

  passwordRecovery(email) {
    const recaptcha = this.recaptcha.getResponse();

    passwordRecoveryRequest(email, recaptcha)
      .then(({ success, error }) => {
        if (success) {
          // @TODO: Save email in session for display on confirm page
          this.props.history.push('/forgot-password/email-sent');
        } else {
          this.setState({
            pending: false,
            errors: getErrors(error, 'form'),
          });
        }
      })
      .catch(err => {
        this.context.captureException(err);
        this.setState({
          errors: getErrors(
            {
              code: 'forgot-password.email.general-error',
            },
            'form'
          ),
        });
      });
  }

  setNewPassword(hash, username, password) {
    const { t } = this.context;
    const recaptcha = this.recaptcha.getResponse();

    setNewPasswordRequest(hash, username, password, recaptcha).then(
      ({ success, error, me }) => {
        if (success) {
          this.props.userReceive(me);

          this.props.promptToast(
            'success',
            t('account', 'login.forgot-password-successful')
          );

          this.props.history.push(getCurrentRef().path);
        } else {
          this.setState({
            pending: false,
            errors: getErrors(error, 'form'),
          });
        }
      }
    );
  }

  validate(inputs, cb) {
    const recaptchaKey = getRecaptchaKey();

    this.setState({
      pending: true,
      errors: {},
    });

    validateInput(inputs).then(({ success, error }) => {
      if (success) {
        if (recaptchaKey && this.props.recaptchaEnabled) {
          this.callback = cb;
          this.recaptcha.reset();
          this.recaptcha.execute();
        } else {
          cb();
        }

        this.setState({
          valid: true,
          pending: !!cb,
          errors: {},
        });
      } else {
        this.setState({
          valid: false,
          pending: false,
          errors: getErrors(error, 'form'),
        });
      }
    });
  }

  login(inputs) {
    this.setState({
      socialPending: false,
      pending: true,
      errors: {},
    });

    const { t, captureException } = this.context;

    loginWithCredentials(inputs)
      .then(({ error }) => {
        if (error) {
          throw new Error(error.code);
        }

        return fetchMe();
      })
      .then(({ me, success }) => {
        if (!me || !success) {
          throw new Error('Failed fetch me');
        }

        this.props.userReceive(me);

        this.setState({
          pending: false,
        });

        this.props.promptToast('success', t('account', 'login.successful'));
        this.props.history.push(getCurrentRef().path);
      })
      .catch(error => {
        captureException(error);
        this.setState({
          pending: false,
          errors: getErrors(error, 'form'),
        });
      });
  }

  clearErrors(name) {
    this.setState(prevState => ({
      errors: {
        ...prevState.errors,
        [name]: undefined,
      },
    }));
  }

  render() {
    const { t, renderMode } = this.context;
    const recaptchaKey = getRecaptchaKey();
    const { pending, socialPending, errors, valid } = this.state;
    const { routeName } = this.props;

    // Converts camelCase route to dash-case
    const translationKey = routeName
      .replace(/([a-zA-Z])(?=[A-Z])/g, '$1-')
      .toLowerCase();
    const heading = t('account', `${translationKey}.title`);

    const isNative = renderMode === 'native';

    return (
      <section>
        <PageContent>
          {!isNative && (
            <Breadcrumb
              homeText={t('common', 'breadcrumb.home')}
              path={[
                {
                  id: '0',
                  name: heading,
                  pathName: translationKey,
                },
              ]}
              firstPathSelectable
            />
          )}

          <PageHeader title={heading}>
            {this.props.subHeading && (
              <p>{t('account', `${translationKey}.subtitle`)}</p>
            )}
          </PageHeader>

          {this.renderView({
            valid,
            pending,
            errors,
            isValidEmail,
            isCapsLockOn,
            socialPending,
            clearErrors: name => this.clearErrors(name),
            createUser: inputs => this.createUser(inputs),
            login: inputs => this.login(inputs),
            validate: (inputs, cb) => this.validate(inputs, cb),
            onSocialInit: () => this.socialInit(),
            onSocialResponse: (err, result) => this.socialResponse(err, result),
            passwordRecovery: email => this.passwordRecovery(email),
            setNewPassword: (hash, username, password) =>
              this.setNewPassword(hash, username, password),
            infoPageMap: this.props.account.infoPageMap,
          })}
        </PageContent>
        {recaptchaKey && this.props.recaptchaEnabled && (
          <Recaptcha
            ref={ref => {
              this.recaptcha = ref;
            }}
            sitekey={recaptchaKey}
            onResolved={() => {
              if (this.callback) {
                this.callback();
                this.callback = undefined;
              }
            }}
          />
        )}
      </section>
    );
  }
}
const { func, oneOf, shape, object, bool } = PropTypes;

AccountManager.propTypes = {
  routeName: oneOf([
    'signup',
    'login',
    'forgotPassword',
    'forgotPasswordChange',
  ]).isRequired,
  account: shape({
    infoPageMap: object,
  }).isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  recaptchaEnabled: bool,
  userReceive: func.isRequired,
  subHeading: bool,
  promptToast: func.isRequired,
};

AccountManager.defaultProps = {
  recaptchaEnabled: true,
  subHeading: false,
};

AccountManager.contextType = AppContext;

export default connect(
  state => state.areas,
  {
    ...accountActions,
    promptToast,
  }
)(withRouter(AccountManager));
